import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, ProfileAPI, AuthAPI } from "@/lib/api";
import { useAuth, useTheme } from "@/lib/providers";
import { toast } from "sonner";
import WalletCard from "@/components/WalletCard";

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const { theme } = useTheme();
  const themes = ["dark"];
  const setTheme = () => {}; // dark-only site — theme picker removed
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [banner, setBanner] = useState(null);
  const [bcc, setBcc] = useState("");
  const [savingBcc, setSavingBcc] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/oauth/gmail/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    load();
    setBcc(user?.bcc_email || "");
    const params = new URLSearchParams(location.search);
    const g = params.get("gmail");
    if (g === "connected") setBanner({ kind: "ok", text: "Gmail connected. You can now send Verdict reports from your Google account." });
    if (g === "error") setBanner({ kind: "err", text: `Gmail couldn't be connected (${params.get("reason") || "unknown"}). Try again.` });
    if (g) navigate("/settings", { replace: true });
  }, [user?.bcc_email, location.search, load, navigate]);

  const connect = async () => {
    setConnecting(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const o = encodeURIComponent(window.location.origin);
      const { data } = await api.get(`/oauth/gmail/connect?origin=${o}`);
      window.location.href = data.auth_url;
    } catch (e) {
      setBanner({ kind: "err", text: e?.response?.data?.detail || e.message });
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/oauth/gmail/disconnect");
      await load();
      setBanner({ kind: "ok", text: "Gmail disconnected." });
    } catch (e) {
      setBanner({ kind: "err", text: e?.response?.data?.detail || e.message || "Disconnect failed" });
    }
  };

  const saveBcc = async () => {
    setSavingBcc(true);
    try {
      await ProfileAPI.patch({ bcc_email: bcc });
      await refreshUser();
      setBanner({ kind: "ok", text: bcc ? `BCC saved · ${bcc} will be copied on every Verdict email.` : "BCC cleared." });
    } catch (e) {
      setBanner({ kind: "err", text: e?.response?.data?.detail || e.message || "Failed to save BCC" });
    } finally {
      setSavingBcc(false);
    }
  };

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <div className="overline" data-testid="settings-overline">Profile · {user?.name}</div>
      <h1 className="display" style={{ fontSize: "clamp(40px,5vw,64px)", lineHeight: 1, marginTop: 12 }}>Settings</h1>

      {banner && (
        <div className="v-surface" data-testid="settings-banner" style={{ padding: 16, marginTop: 24, borderColor: banner.kind === "err" ? "var(--v-accent)" : "var(--v-border)" }}>
          <div className="mono" style={{ fontSize: 12, color: banner.kind === "err" ? "var(--v-accent)" : "var(--v-text)" }}>{banner.text}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" style={{ marginTop: 40 }}>
        {user?.user_type === "external" && user?.role !== "admin" && <WalletCard />}
        <div className="v-surface" style={{ padding: 28 }} data-testid="profile-card">
          <div className="overline">Account</div>
          <h3 style={{ marginTop: 8, fontSize: 24 }}>{user?.name}</h3>
          <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 4 }}>{user?.email}</div>
          <div className="v-chip" style={{ marginTop: 12 }}>{user?.role}</div>
          <div style={{ marginTop: 28 }}>
            <button className="v-btn v-btn-outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="settings-logout-btn">Sign out</button>
          </div>
        </div>

        <div className="v-surface" style={{ padding: 28 }} data-testid="gmail-card">
          <div className="overline">Send reports from your Gmail</div>
          <h3 style={{ marginTop: 8, fontSize: 24 }}>Gmail integration</h3>
          <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.55 }}>
            Connect your Google account so Verdict mails assessment reports from your address — not from a generic studio inbox. Only the <span className="mono">gmail.send</span> scope is requested.
          </p>

          {status === null ? (
            <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 16 }}>Checking…</div>
          ) : status.connected ? (
            <div style={{ marginTop: 20 }} data-testid="gmail-connected-state">
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", borderRadius: 8,
                background: "rgba(6, 182, 212, 0.10)",
                border: "1px solid rgba(6, 182, 212, 0.35)",
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: "#06B6D4", boxShadow: "0 0 0 4px rgba(6,182,212,0.18)",
                  flexShrink: 0,
                }} aria-hidden />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }} data-testid="gmail-connected-chip">
                    Gmail connected
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
                    Sending as <b style={{ color: "var(--v-text)" }}>{status.email}</b>
                    {status.updated_at && <span> · since {new Date(status.updated_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="v-btn v-btn-outline" onClick={disconnect} data-testid="gmail-disconnect-btn">Disconnect</button>
                <button className="v-btn v-btn-ghost" onClick={connect} disabled={connecting} data-testid="gmail-reconnect-btn">
                  {connecting ? "Redirecting…" : "Reconnect a different account"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 20 }}>
              <button className="v-btn" onClick={connect} disabled={connecting} data-testid="gmail-connect-btn">
                {connecting ? "Redirecting…" : "Connect Gmail →"}
              </button>
            </div>
          )}
        </div>

        <div className="v-surface" style={{ padding: 28 }} data-testid="bcc-card">
          <div className="overline">Auto-BCC</div>
          <h3 style={{ marginTop: 8, fontSize: 24 }}>Loop in your studio lead</h3>
          <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.55 }}>
            Every Verdict report you mail will be silently copied to this address — a free paper trail for your account manager or studio lead. Leave empty to disable.
          </p>
          <input
            className="v-input"
            type="email"
            placeholder="lead@studio.com"
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
            data-testid="bcc-input"
            style={{ marginTop: 14 }}
          />
          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <button className="v-btn" onClick={saveBcc} disabled={savingBcc} data-testid="bcc-save-btn">
              {savingBcc ? "Saving…" : "Save BCC"}
            </button>
            {bcc && (
              <button className="v-btn v-btn-outline" onClick={() => { setBcc(""); }} data-testid="bcc-clear-btn">Clear</button>
            )}
          </div>
          {user?.bcc_email && (
            <div className="mono" style={{ marginTop: 12, fontSize: 11, color: "var(--v-text-2)" }}>
              Currently BCC'ing · {user.bcc_email}
            </div>
          )}
        </div>

        <div className="v-surface" style={{ padding: 28 }} data-testid="info-card">
          <div className="overline">About</div>
          <h3 style={{ marginTop: 8, fontSize: 24 }}>Verdict for India</h3>
          <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.55 }}>
            Verdict critiques packaging across 7 dimensions tuned for the Indian market — e-commerce thumbnails, kirana shelves, regional cues, distinctiveness, hierarchy, audience fit and brand alignment. Project memory persists so refreshes and new variants can be compared against the parent design.
          </p>
        </div>

        {user?.is_super_admin && (
          <SuperAdminPasswordCard />
        )}
      </div>
    </div>
  );
}

function SuperAdminPasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Confirmation doesn't match");
      return;
    }
    setBusy(true);
    try {
      await AuthAPI.superAdminPassword(current, next);
      toast.success("Super-admin password rotated. Share the new one with the studio lead.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rotate password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v-surface" style={{ padding: 28, borderColor: "var(--v-accent)" }} data-testid="super-admin-password-card">
      <div className="overline">Super-admin · Password</div>
      <h3 style={{ marginTop: 8, fontSize: 24 }}>Rotate the master password</h3>
      <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.55 }}>
        Anyone with this password can sign in as super-admin from the bottom of the landing page.
        Rotate it whenever a team member leaves or the password may have been shared too widely.
      </p>
      <form onSubmit={onSubmit} style={{ marginTop: 20, display: "grid", gap: 14, maxWidth: 460 }}>
        <div>
          <div className="overline">Current password</div>
          <input
            type="password"
            className="v-input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            data-testid="sa-current-password"
            required
          />
        </div>
        <div>
          <div className="overline">New password (min. 8 chars)</div>
          <input
            type="password"
            className="v-input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            data-testid="sa-new-password"
            required
          />
        </div>
        <div>
          <div className="overline">Confirm new password</div>
          <input
            type="password"
            className="v-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="sa-confirm-password"
            required
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="submit" className="v-btn" disabled={busy} data-testid="sa-rotate-btn">
            {busy ? "Rotating…" : "Rotate password"}
          </button>
        </div>
      </form>
    </div>
  );
}
